import React, { useCallback, useEffect, useState } from 'react';
import { Card } from '../../../components/ui/Card';
import { Button } from '../../../components/ui/Button';
import { Badge } from '../../../components/ui/Badge';
import { Input } from '../../../components/ui/Input';
import { Modal } from '../../../components/ui/Modal';
import { EmptyState } from '../../../components/ui/EmptyState';
import { Spinner } from '../../../components/ui/Spinner';
import { useToast } from '../../../hooks/useToast';
import {
  AdminPlan,
  CouponInput,
  CouponRedemption,
  PlanCoupon,
  formatKobo,
  plansApi,
} from '../../../api/plans';

const date = (iso: string | null) =>
  iso ? new Date(iso).toLocaleDateString('en-NG', { day: 'numeric', month: 'short', year: 'numeric' }) : '—';

const th: React.CSSProperties = {
  padding: '10px 12px',
  textAlign: 'left',
  fontSize: 12,
  color: 'var(--neutral-600)',
  textTransform: 'uppercase',
  letterSpacing: 0.4,
};
const td: React.CSSProperties = { padding: '10px 12px', fontSize: 13, verticalAlign: 'top' };
const fieldStyle: React.CSSProperties = {
  height: 38,
  padding: '0 10px',
  fontSize: 14,
  fontFamily: 'var(--font-family)',
  color: 'var(--neutral-800)',
  background: 'var(--neutral-100)',
  border: '1px solid var(--neutral-300)',
  borderRadius: 'var(--radius-md)',
};
const label: React.CSSProperties = { display: 'flex', flexDirection: 'column', gap: 6, fontSize: 13, fontWeight: 500, color: 'var(--neutral-700)' };

const TERMS = [
  { value: 'monthly', label: 'Monthly' },
  { value: 'annual', label: 'Yearly' },
];

/**
 * Codes a business types when buying a plan, for money off.
 *
 * The discount itself is fixed once made: businesses may already have used
 * it on that understanding. What can change is the description, whether it
 * is on, how many more businesses can use it, and when it ends.
 */
export const Coupons: React.FC<{ plans: AdminPlan[]; canManage: boolean }> = ({ plans, canManage }) => {
  const toast = useToast();
  const paid = plans.filter((p) => p.monthlyPriceKobo > 0 && p.status !== 'retired');
  const [coupons, setCoupons] = useState<PlanCoupon[] | null>(null);
  const [creating, setCreating] = useState(false);
  const [viewing, setViewing] = useState<PlanCoupon | null>(null);
  const [redemptions, setRedemptions] = useState<CouponRedemption[] | null>(null);

  const load = useCallback(async () => {
    try {
      setCoupons(await plansApi.coupons());
    } catch (err) {
      toast.error(err, 'Could not load coupons.');
    }
  }, [toast]);

  useEffect(() => {
    void load();
  }, [load]);

  async function toggle(c: PlanCoupon) {
    try {
      await plansApi.updateCoupon(c.id, { active: !c.active });
      toast.success(c.active ? `${c.code} switched off.` : `${c.code} switched on.`);
      await load();
    } catch (err) {
      toast.error(err, 'Could not change that coupon.');
    }
  }

  async function open(c: PlanCoupon) {
    setViewing(c);
    setRedemptions(null);
    try {
      setRedemptions(await plansApi.redemptions(c.id));
    } catch (err) {
      toast.error(err, 'Could not load who used it.');
      setRedemptions([]);
    }
  }

  const planName = (id: string) => plans.find((p) => p.id === id)?.name ?? 'A removed plan';
  const limits = (c: PlanCoupon) =>
    [
      c.planIds.length ? c.planIds.map(planName).join(', ') : 'Any plan',
      c.terms.length ? c.terms.map((t) => (t === 'annual' ? 'yearly' : t)).join(' or ') : 'any term',
    ].join(', ');

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
      <Card padding={0}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '14px 16px' }}>
          <div>
            <h3 style={{ margin: 0, fontSize: 15 }}>Coupons</h3>
            <p style={{ margin: '4px 0 0', fontSize: 13, color: 'var(--neutral-600)' }}>
              Businesses enter these when they buy a plan. Each business can use a code once.
            </p>
          </div>
          {canManage && <Button onClick={() => setCreating(true)}>New coupon</Button>}
        </div>
        {coupons === null ? (
          <div style={{ display: 'flex', justifyContent: 'center', padding: 32 }}>
            <Spinner />
          </div>
        ) : coupons.length === 0 ? (
          <EmptyState title="No coupons yet." />
        ) : (
          <div style={{ overflowX: 'auto' }}>
            <table style={{ width: '100%', minWidth: 860, borderCollapse: 'collapse' }}>
              <thead>
                <tr style={{ background: 'var(--neutral-200)' }}>
                  {['Code', 'Discount', 'For', 'Used', 'Ends', 'Status', ''].map((h) => (
                    <th key={h} style={th}>
                      {h}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {coupons.map((c) => (
                  <tr key={c.id} style={{ borderTop: '1px solid var(--neutral-300)' }}>
                    <td style={td}>
                      <div style={{ fontWeight: 700, fontFamily: 'monospace', fontSize: 14 }}>{c.code}</div>
                      {c.description && <div style={{ fontSize: 12, color: 'var(--neutral-500)' }}>{c.description}</div>}
                    </td>
                    <td style={td}>{c.summary}</td>
                    <td style={td}>{limits(c)}</td>
                    <td style={td}>
                      {c.redemptions}
                      {c.maxRedemptions != null ? ` of ${c.maxRedemptions}` : ''}
                    </td>
                    <td style={td}>{date(c.expiresAt)}</td>
                    <td style={td}>
                      <Badge variant={c.active ? 'success' : 'neutral'}>{c.active ? 'On' : 'Off'}</Badge>
                    </td>
                    <td style={{ ...td, whiteSpace: 'nowrap' }}>
                      <Button size="sm" variant="ghost" onClick={() => void open(c)}>
                        Who used it
                      </Button>
                      {canManage && (
                        <Button size="sm" variant="ghost" onClick={() => void toggle(c)}>
                          {c.active ? 'Switch off' : 'Switch on'}
                        </Button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Card>

      <CouponForm
        open={creating}
        plans={paid}
        onClose={() => setCreating(false)}
        onCreated={() => {
          setCreating(false);
          void load();
        }}
      />

      <Modal isOpen={!!viewing} onClose={() => setViewing(null)} title={`Who used ${viewing?.code ?? ''}`} maxWidth={560}>
        {redemptions === null ? (
          <div style={{ display: 'flex', justifyContent: 'center', padding: 24 }}>
            <Spinner />
          </div>
        ) : redemptions.length === 0 ? (
          <div style={{ fontSize: 13, color: 'var(--neutral-600)' }}>Nobody yet.</div>
        ) : (
          <table style={{ width: '100%', borderCollapse: 'collapse' }}>
            <thead>
              <tr>
                {['Business', 'Saved so far', 'Payments left', 'Used'].map((h) => (
                  <th key={h} style={th}>
                    {h}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {redemptions.map((r) => (
                <tr key={r.id} style={{ borderTop: '1px solid var(--neutral-300)' }}>
                  <td style={td}>{r.businessName}</td>
                  <td style={td}>{formatKobo(r.discountKoboTotal)}</td>
                  <td style={td}>{r.paymentsLeft === null ? 'Every payment' : r.paymentsLeft}</td>
                  <td style={td}>{date(r.createdAt)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </Modal>
    </div>
  );
};

const CouponForm: React.FC<{
  open: boolean;
  plans: AdminPlan[];
  onClose: () => void;
  onCreated: () => void;
}> = ({ open, plans, onClose, onCreated }) => {
  const toast = useToast();
  const [code, setCode] = useState('');
  const [description, setDescription] = useState('');
  const [kind, setKind] = useState<'percent' | 'amount'>('percent');
  const [value, setValue] = useState('');
  const [duration, setDuration] = useState<'once' | 'repeating' | 'forever'>('once');
  const [payments, setPayments] = useState('3');
  const [planIds, setPlanIds] = useState<string[]>([]);
  const [terms, setTerms] = useState<string[]>([]);
  const [max, setMax] = useState('');
  const [expires, setExpires] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!open) return;
    setCode('');
    setDescription('');
    setKind('percent');
    setValue('');
    setDuration('once');
    setPayments('3');
    setPlanIds([]);
    setTerms([]);
    setMax('');
    setExpires('');
    setError(null);
  }, [open]);

  const toggleIn = (list: string[], v: string) => (list.includes(v) ? list.filter((x) => x !== v) : [...list, v]);

  async function save() {
    const cleanCode = code.toUpperCase().replace(/[^A-Z0-9]/g, '');
    const n = Number(value.replace(/[₦,\s%]/g, ''));
    if (cleanCode.length < 3) return setError('The code needs at least 3 letters or numbers.');
    if (kind === 'percent' && !(Number.isInteger(n) && n >= 1 && n <= 100)) return setError('Percent off is 1 to 100.');
    if (kind === 'amount' && !(n >= 1)) return setError('Enter the naira off, at least ₦1.');
    const p = Number(payments);
    if (duration === 'repeating' && !(Number.isInteger(p) && p >= 2 && p <= 36)) {
      return setError('Say how many payments: 2 to 36.');
    }
    const m = max.trim() ? Number(max) : undefined;
    if (m !== undefined && !(Number.isInteger(m) && m >= 1)) return setError('The limit is a whole number of businesses.');
    setError(null);
    const input: CouponInput = {
      code: cleanCode,
      ...(description.trim() ? { description: description.trim() } : {}),
      kind,
      ...(kind === 'percent' ? { percentOff: n } : { amountOffKobo: Math.round(n * 100) }),
      duration,
      ...(duration === 'repeating' ? { durationPayments: p } : {}),
      ...(planIds.length ? { planIds } : {}),
      ...(terms.length ? { terms } : {}),
      ...(m !== undefined ? { maxRedemptions: m } : {}),
      ...(expires ? { expiresAt: new Date(`${expires}T23:59:59`).toISOString() } : {}),
    };
    setSaving(true);
    try {
      await plansApi.createCoupon(input);
      toast.success(`${cleanCode} is ready to use.`);
      onCreated();
    } catch (err) {
      toast.error(err, 'Could not make that coupon.');
    } finally {
      setSaving(false);
    }
  }

  return (
    <Modal isOpen={open} onClose={onClose} title="New coupon" maxWidth={560}>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
        <Input label="Code" value={code} maxLength={32} placeholder="LAUNCH20" onChange={(e) => setCode(e.target.value.toUpperCase())} />
        <Input
          label="Description (optional)"
          value={description}
          maxLength={200}
          placeholder="Launch offer"
          hint="Shown to the business when the code works."
          onChange={(e) => setDescription(e.target.value)}
        />
        <div style={{ display: 'flex', gap: 12 }}>
          <label style={label}>
            Discount
            <select aria-label="Discount kind" value={kind} onChange={(e) => setKind(e.target.value as 'percent' | 'amount')} style={fieldStyle}>
              <option value="percent">Percent off</option>
              <option value="amount">Naira off</option>
            </select>
          </label>
          <div style={{ flex: 1 }}>
            <Input
              label={kind === 'percent' ? 'Percent' : 'Naira'}
              value={value}
              inputMode="decimal"
              placeholder={kind === 'percent' ? '20' : '5000'}
              onChange={(e) => setValue(e.target.value)}
            />
          </div>
        </div>
        <div style={{ display: 'flex', gap: 12, alignItems: 'flex-end' }}>
          <label style={label}>
            For
            <select
              aria-label="How long"
              value={duration}
              onChange={(e) => setDuration(e.target.value as 'once' | 'repeating' | 'forever')}
              style={fieldStyle}
            >
              <option value="once">The first payment</option>
              <option value="repeating">The first few payments</option>
              <option value="forever">Every payment</option>
            </select>
          </label>
          {duration === 'repeating' && (
            <div style={{ width: 140 }}>
              <Input label="Payments" type="number" min={2} max={36} value={payments} onChange={(e) => setPayments(e.target.value)} />
            </div>
          )}
        </div>

        <div style={label}>
          Plans
          <div style={{ display: 'flex', gap: 14, flexWrap: 'wrap', fontWeight: 400 }}>
            {plans.map((p) => (
              <label key={p.id} style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
                <input type="checkbox" checked={planIds.includes(p.id)} onChange={() => setPlanIds((l) => toggleIn(l, p.id))} />
                {p.name}
              </label>
            ))}
          </div>
          <span style={{ fontWeight: 400, fontSize: 12, color: 'var(--neutral-500)' }}>None ticked: every plan.</span>
        </div>
        <div style={label}>
          Paying
          <div style={{ display: 'flex', gap: 14, fontWeight: 400 }}>
            {TERMS.map((t) => (
              <label key={t.value} style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
                <input type="checkbox" checked={terms.includes(t.value)} onChange={() => setTerms((l) => toggleIn(l, t.value))} />
                {t.label}
              </label>
            ))}
          </div>
          <span style={{ fontWeight: 400, fontSize: 12, color: 'var(--neutral-500)' }}>None ticked: monthly or yearly.</span>
        </div>
        <div style={{ display: 'flex', gap: 12 }}>
          <div style={{ flex: 1 }}>
            <Input label="Most businesses (optional)" type="number" min={1} value={max} placeholder="No limit" onChange={(e) => setMax(e.target.value)} />
          </div>
          <div style={{ flex: 1 }}>
            <Input label="Last day (optional)" type="date" value={expires} onChange={(e) => setExpires(e.target.value)} />
          </div>
        </div>

        {error && <div style={{ color: 'var(--danger-900)', fontSize: 13 }}>{error}</div>}
        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8 }}>
          <Button variant="ghost" onClick={onClose} disabled={saving}>
            Cancel
          </Button>
          <Button onClick={() => void save()} loading={saving} disabled={saving}>
            Make coupon
          </Button>
        </div>
      </div>
    </Modal>
  );
};
